/** Mirrors backend AuthLoginErrorBody.
 * Nest returns this object as the full 401 JSON body when thrown as UnauthorizedException(object).
 */
export interface AuthLoginErrorBody {
  code?: string;
  message?: string;
  remainingAttempts?: number;
  maxLoginAttempts?: number;
  statusCode?: number;
  error?: string;
}

const ACCOUNT_LOCKED_FALLBACK =
  'Cuenta bloqueada por múltiples intentos fallidos. Contacte al administrador para desbloquearla.';

function isAuthLoginErrorBody(value: unknown): value is AuthLoginErrorBody {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function hasStructuredLoginFields(value: AuthLoginErrorBody): boolean {
  return (
    typeof value.code === 'string' ||
    typeof value.remainingAttempts === 'number' ||
    typeof value.maxLoginAttempts === 'number'
  );
}

/**
 * Build a user-facing login error string from a Nest 401 body.
 * Handles: structured object body, classic `{ message: string }`, string, or string[].
 */
export function formatLoginErrorMessage(raw: unknown): string {
  if (raw == null) {
    return 'Error al iniciar sesión.';
  }

  if (Array.isArray(raw)) {
    const first = raw[0];
    return typeof first === 'string' ? first : formatLoginErrorMessage(first);
  }

  if (typeof raw === 'string') {
    return raw;
  }

  if (!isAuthLoginErrorBody(raw)) {
    return 'Error al iniciar sesión.';
  }

  // Nest object exception → body IS the AuthLoginErrorBody (possibly without statusCode).
  // Classic string exception → body is { statusCode, message: string, error }.
  const payload: AuthLoginErrorBody = hasStructuredLoginFields(raw)
    ? raw
    : typeof raw.message === 'object' && raw.message != null
      ? (raw.message as AuthLoginErrorBody)
      : raw;

  const baseMessage =
    typeof payload.message === 'string' && payload.message.length > 0
      ? payload.message
      : typeof raw.message === 'string' && raw.message.length > 0
        ? raw.message
        : 'Credenciales inválidas';

  if (
    payload.code === 'ACCOUNT_LOCKED' ||
    (typeof payload.remainingAttempts === 'number' && payload.remainingAttempts <= 0)
  ) {
    return baseMessage.includes('bloqueada') ? baseMessage : ACCOUNT_LOCKED_FALLBACK;
  }

  if (typeof payload.remainingAttempts === 'number' && payload.remainingAttempts > 0) {
    const n = payload.remainingAttempts;
    const label = n === 1 ? 'intento' : 'intentos';
    return `${baseMessage}. Te quedan ${n} ${label}.`;
  }

  return baseMessage;
}
